import { useState, useEffect, useCallback } from 'react';
import { apiFetch, apiImageUrl } from '../utils/streamApi';
import '../css/parvekshak.css';
import '../css/knownfaces.css';

// PRVSK-64 — standalone "Add to Known" screen, reachable from the app menu
// independent of any alert. Capture from a camera only, with the raise-a-hand
// liveness check (the photo-upload option was removed). Saves through the
// backend's validated, matcher-compatible path
// (_api_save_face_to_known via /faces/known/{name}/capture) —
// NOT the legacy /faces/known/{name}/photos endpoint KnownFacesPage.jsx's
// "Add Person" modal uses, which writes a raw file the matcher never reads.

function Toast({ msg, type }) {
  if (!msg) return null;
  return <div className={`pk-toast ${type}`}>{msg}</div>;
}

// Shared card/label/input look — mirrors AttendanceConfigPage's local style
// constants so this screen reads as part of the same app on any screen size,
// instead of bare labels/inputs floating with no visual grouping (the mobile
// margin/organisation issue this replaces).
const card  = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginTop: 12 };
const label = { fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6, display: 'block' };

// Live camera preview — polls a fresh JPEG frame, same pattern as
// AttendanceConfigPage's OnboardPreview (not reused directly: that component
// isn't exported, and duplicating ~15 lines here is cheaper than exporting a
// prop-incompatible component across modules).
function CapturePreview({ cameraIdx, ready }) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    let alive = true;
    const tick = () => apiImageUrl(`/cameras/${cameraIdx}/frame?ts=${Date.now()}`)
      .then(u => { if (alive) setUrl(u); }).catch(() => {});
    tick();
    const t = setInterval(tick, 800);
    return () => { alive = false; clearInterval(t); };
  }, [cameraIdx]);
  return (
    <div style={{
      background: '#000', borderRadius: 8, overflow: 'hidden', maxWidth: 480,
      border: `2px solid ${ready ? '#5cc98a' : 'var(--border)'}`,
    }}>
      {url
        ? <img src={url} alt="camera preview" style={{ display: 'block', width: '100%', height: 'auto' }} />
        : <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>Loading camera…</div>}
    </div>
  );
}

export default function AddKnownPersonPage() {
  return (
    <div className="pk-page">
      <div className="page-toolbar">
        <span className="tb-label">Add to Known · Capture from Camera</span>
      </div>
      <AddKnownPersonForm />
    </div>
  );
}

// The form itself (capture from a camera), shared by the menu page
// above and Guided Setup's "Who lives here?" step. onEnrolled(name) fires after
// each successful enrolment so the host can refresh its own people list.
export function AddKnownPersonForm({ onEnrolled }) {
  // Name — pick an existing person to add another sample under, or declare a
  // new one. Free-text-only invited exactly the duplicate-identity bug noted
  // in AttendanceConfigPage's onboarding section (two names for one face).
  const [existingNames, setExistingNames] = useState([]);
  const [pick,    setPick]    = useState('__new__');
  const [newName, setNewName] = useState('');
  const isNew = pick === '__new__';
  const name  = (isNew ? newName : pick).trim();

  const loadNames = useCallback(() => {
    apiFetch('/faces/known').then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setExistingNames((d.persons || []).map(p => p.name)); })
      .catch(() => {});
  }, []);
  useEffect(() => { loadNames(); }, [loadNames]);

  // ── Camera capture ──
  const [cameras, setCameras] = useState([]);
  const [cam,     setCam]     = useState('');
  const [readout, setReadout] = useState(null);
  const hasCam  = cam !== '';
  const raised  = !!(readout?.left_raised || readout?.right_raised);

  useEffect(() => {
    apiFetch('/ui/patrol/form-options').then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setCameras(d.cameras || []); })
      .catch(() => {});
  }, []);

  // Live hand-raise readout — same endpoint/cadence as attendance onboarding,
  // so an admin sees "RAISE A HAND" turn to "READY" before capturing instead
  // of finding out via a blind 422.
  useEffect(() => {
    if (!hasCam) { setReadout(null); return; }
    let alive = true, working = false;
    const poll = async () => {
      if (working) return;
      working = true;
      try {
        const r = await apiFetch('/ui/patrol/test', { method: 'POST', json: { camera_idx: Number(cam), polygon: [] } });
        if (alive) setReadout(r.ok ? await r.json() : null);
      } catch { /* transient */ } finally { working = false; }
    };
    poll();
    const t = setInterval(poll, 800);
    return () => { alive = false; clearInterval(t); };
  }, [cam, hasCam]);

  const [busy,  setBusy]  = useState(false);
  const [toast, setToast] = useState(null);
  const showToast = (msg, type = 'ok') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3200); };

  const resetAfterSave = (enrolledName) => {
    setNewName('');
    setPick(enrolledName);   // next sample defaults to "add another" for the same person
    loadNames();
    if (onEnrolled) onEnrolled(enrolledName);
  };

  const doCapture = async () => {
    if (!name) { showToast(isNew ? 'Enter a name' : 'Select who this is', 'err'); return; }
    setBusy(true);
    try {
      const r = await apiFetch(`/faces/known/${encodeURIComponent(name)}/capture`, {
        method: 'POST', json: { camera_idx: Number(cam) },
      });
      const d = await r.json().catch(() => null);
      if (!r.ok) throw new Error(d?.detail || `Capture failed (${r.status})`);
      showToast(`${name} enrolled`);
      resetAfterSave(name);
    } catch (err) {
      showToast(err.message || 'Capture failed', 'err');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div style={{ maxWidth: 640 }}>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '12px 0 0', lineHeight: 1.5 }}>
          🎥 The person stands in front of a camera and raises a hand — their face is
          captured once the camera sees them. Add 2–3 captures per person for better recognition.
        </p>

        {/* ── Who ── */}
        <div style={card}>
          <label style={label}>Who</label>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <select value={pick} onChange={e => setPick(e.target.value)}
              className="alert-inline-input" style={{ padding: '9px 12px', fontSize: 14, minWidth: 200, flex: '1 1 200px' }}>
              <option value="__new__">+ New person…</option>
              {existingNames.length > 0 && (
                <optgroup label="Add another sample">
                  {existingNames.map(n => <option key={n} value={n}>{n}</option>)}
                </optgroup>
              )}
            </select>
            {isNew && (
              <input
                className="alert-inline-input"
                style={{ padding: '9px 12px', fontSize: 14, flex: '1 1 180px' }}
                placeholder="Full name"
                value={newName}
                onChange={e => setNewName(e.target.value)}
              />
            )}
          </div>
        </div>

        {/* ── Camera ── */}
        <div style={card}>
            <label style={label}>Camera</label>
            <select value={cam} onChange={e => setCam(e.target.value)}
              className="alert-inline-input" style={{ padding: '9px 12px', fontSize: 14, width: '100%', maxWidth: 320 }}>
              <option value="">Select camera…</option>
              {cameras.map(c => <option key={c.idx} value={c.idx}>{c.name}</option>)}
            </select>

            {hasCam && (
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start', marginTop: 16 }}>
                <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                  <CapturePreview cameraIdx={cam} ready={raised} />
                </div>
                <div style={{ flex: '1 1 200px', minWidth: 200 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                    <span style={{ color: 'var(--text-muted)' }}>Person detected</span>
                    <span style={{ fontWeight: 700, color: readout?.person_detected ? '#5cc98a' : 'var(--text)' }}>
                      {readout?.person_detected ? 'YES' : 'NO'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                    <span style={{ color: 'var(--text-muted)' }}>Hand raised</span>
                    <span style={{ fontWeight: 700, color: raised ? '#5cc98a' : 'var(--text)' }}>
                      {raised ? 'RAISED' : 'NOT RAISED'}
                    </span>
                  </div>
                  <div style={{
                    marginTop: 12, padding: '10px 14px', borderRadius: 10, textAlign: 'center', fontWeight: 800,
                    background: raised ? 'rgba(92,201,138,0.92)' : 'rgba(40,40,40,0.6)',
                    color: raised ? '#08130c' : 'var(--text-muted)',
                    border: `1px solid ${raised ? '#5cc98a' : 'var(--border)'}`,
                  }}>
                    {raised ? 'READY' : 'RAISE A HAND'}
                  </div>
                  <button onClick={doCapture} disabled={busy || !raised || !name}
                    className="pk-btn primary" style={{ width: '100%', marginTop: 10, opacity: (busy || !raised || !name) ? 0.5 : 1 }}>
                    {busy ? '⏳ Capturing…' : '✓ Capture & Enrol'}
                  </button>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
                    Raising a hand confirms someone is really in front of the camera — the same
                    liveness check used for attendance onboarding.
                  </div>
                </div>
              </div>
            )}
        </div>
      </div>

      <Toast msg={toast?.msg} type={toast?.type} />
    </>
  );
}
